<template>
  <transition name="modal-animation">
    <div class="modal" @click="outSideClick" v-show="uiOptions().isModalOpen[id]" :id="id">
      <transition name="modal-animation-inner">
        <div class="modal-inner" :class="modalSize" v-show="uiOptions().isModalOpen[id]">
          <button type="button" class="close button small clear icon" @click="uiOptions().toggleModal(id)">
            <i class="bi bi-x-lg" aria-hidden="true"> </i>
            <span class="sr-only"> {{ $t('messages.modals.actions.close') }} </span>
          </button>

          <!-- Modal Content -->
          <div class="modal-header">
            <slot name="modal-header"> </slot>
          </div>
          <!-- Modal Body -->
          <div class="modal-body">
            <slot name="modal-body"> </slot>
          </div>
          <!-- Modal-Footer -->
          <div class="modal-footer">
            <div class="grid-container">
              <div class="grid-x grid-padding-x">
                <slot name="modal-footer"> </slot>
              </div>
            </div>
          </div>
          <!-- Modal Multi-Form Buttons -->
          <div class="modal-multi-form-buttons">
            <slot name="modal-multi-form-buttons"> </slot>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script lang="ts">
import { uiOptions } from '@/config'
import { computed, defineComponent, PropType, watch, watchEffect } from 'vue'

export default defineComponent({
  name: 'Modal',
  props: {
    id: {
      type: String as PropType<string>,
      required: true,
    },
  },
  setup(props, context) {
    uiOptions().isModalOpen[props.id] = false

    const outSideClick = (e: Event) => {
      const target = e.target as HTMLElement
      if (target.className === 'modal') {
        uiOptions().toggleModal(props.id)
      }
    }

    //NOTE - Possible sizes: medium-modal, and big-modal; default small
    const modalSize = computed(() => (context.attrs.size ? context.attrs.size : ''))

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        uiOptions().isModalOpen[props.id] = false
        document.removeEventListener('keydown', handler)
      }
    }

    const handleEsc = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        uiOptions().toggleModal(props.id)
      }
    }
    watchEffect(() => {
      if (uiOptions().isModalOpen[props.id]) {
        document.addEventListener('keyup', handleEsc)
      } else {
        document.removeEventListener('keyup', handleEsc)
      }
    })

    const modal = computed(() => uiOptions().isModalOpen[props.id])
    watch(modal, (newValue) => {
      if (newValue) {
        document.addEventListener('keydown', handler)
      }
    })

    return { modalSize, outSideClick, uiOptions }
  },
})
</script>

<style lang="scss" scoped>
.modal-animation-enter-active,
.modal-animation-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.02);
}

.modal-animation-enter-from,
.modal-animation-leave-to {
  opacity: 0;
}

.modal-animation-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.02) 0.15s;
}

.modal-animation-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.02);
}

.modal-animation-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.modal-animation-inner-leave-to {
  transform: scale(0.8);
}
</style>
